import type { Ref } from "vue";
import { redactGuardianPhone } from "@/helper/permissionHelper";
import { HttpClient } from "@/services/httpClient";
import {
  fetchFrappeDocCount,
  normalizeFrappeListResult,
  withFrappeLoading,
  type FrappeListResult,
} from "@/services/frappe/list";
import { Controllers, FrappeMethodType } from "@/type/api";
import type { FrappeDoctypeLookupOption } from "@/api/frappeLookupApi";
import { GetPaymentEntries } from "@/api/paymentEntryApi";
import { fetchOrders } from "@/api/orderApi";
import { GetSalesInvoices } from "@/api/salesInvoiceApi";
import {
  buildFrappeLikeSearchFilters,
  serializeFrappeFilters,
  type FrappeFilterClause,
} from "@/services/frappe/filters";
// READ-ONLY coupon shape, for the customer summary counts below. There is no coupon
// WRITE in this module any more — see fetchCustomerCoupons for the one read that remains
// and why it is still a doctype query.
import type { Coupon } from "@/type/coupon";
import type {
  AddressPayload,
  CustomerAddress,
  CustomerCouponPayload,
  CustomerDetail,
  CustomerFinancialSummary,
  CustomerListItem,
  CustomerReferencedItem,
  UpdateCustomerPayload,
} from "@/type/customer";
import type { PaymentEntryListItem } from "@/type/paymentEntry";
import type { SalesInvoiceListItem } from "@/type/salesInvoice";
import { localToday, toLocalDateKey } from "@/utils/localDate";

interface QuickCustomerPayload {
  customerName: string;
  mobileNo?: string | null;
  emailId?: string | null;
}

interface GenericDocRow {
  name?: string | null;
  is_group?: number | string | boolean | null;
}

interface CustomerFilterParam {
  search?: string;
  customerType?: string | null;
  customerGroup?: string | null;
  disabled?: boolean | number | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  filters?: string;
  or_filters?: string;
  limit_start?: number;
  limit_page_length?: number;
  order_by?: string;
}

interface CustomerDocResponse {
  data?: {
    name?: string | null;
    customer_name?: string | null;
    mobile_no?: string | null;
    email_id?: string | null;
  };
  name?: string | null;
  customer_name?: string | null;
  mobile_no?: string | null;
  email_id?: string | null;
}

interface CustomerDoc {
  name?: string | null;
  customer_name?: string | null;
  customer_type?: string | null;
  customer_group?: string | null;
  mobile_no?: string | null;
  email_id?: string | null;
  image?: string | null;
  default_currency?: string | null;
  territory?: string | null;
  disabled?: number | string | boolean | null;
  creation?: string | null;
  modified?: string | null;
}

interface AddressDoc {
  name?: string | null;
  address_title?: string | null;
  address_type?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  country?: string | null;
  is_primary_address?: number | string | boolean | null;
  is_shipping_address?: number | string | boolean | null;
}

interface SalesInvoiceItemRow {
  parent?: string | null;
  item_code?: string | null;
  item_name?: string | null;
  qty?: number | string | null;
  amount?: number | string | null;
}

const CUSTOMER_DOCTYPE = "Customer";

/** The only customer columns offline mode stores. Kept narrow on purpose. */
export interface OfflineCustomerRow {
  name: string;
  customerName: string;
  arabicName: string | null;
  mobileNo: string | null;
  customerGroup: string | null;
  modified: string | null;
}
const ADDRESS_DOCTYPE = "Address";
const DYNAMIC_LINK_DOCTYPE = "Dynamic Link";
const SALES_INVOICE_ITEM_DOCTYPE = "Sales Invoice Item";
const COUPON_DOCTYPE = "Coupon Code";
const CUSTOMER_ACTIVITY_LIMIT = 500;
const CUSTOMER_PREVIEW_LIMIT = 20;

const customerService = new HttpClient(Controllers.customer);
const addressService = new HttpClient(encodeDoctype(ADDRESS_DOCTYPE));
const salesInvoiceItemService = new HttpClient(encodeDoctype(SALES_INVOICE_ITEM_DOCTYPE));
const couponService = new HttpClient(Controllers.coupon);

function encodeDoctype(doctype: string) {
  return encodeURIComponent(doctype) as Controllers;
}

function toBoolean(value: unknown) {
  if (typeof value === "boolean")
    return value;

  return Number(value ?? 0) === 1;
}

function normalizeString(value: unknown) {
  const normalized = String(value ?? "").trim();
  return normalized || null;
}

function toNumber(value: unknown) {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function todayDate() {
  return localToday();
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);

  return next;
}

function formatDate(date: Date) {
  return toLocalDateKey(date);
}

function normalizeCustomerDoc(row: CustomerDoc): CustomerDetail {
  const name = normalizeString(row.name) ?? "";

  return {
    name,
    customerName: normalizeString(row.customer_name) ?? name,
    customerType: normalizeString(row.customer_type),
    customerGroup: normalizeString(row.customer_group),
    mobileNo: redactGuardianPhone(row.mobile_no) || null,
    emailId: normalizeString(row.email_id),
    image: normalizeString(row.image),
    defaultCurrency: normalizeString(row.default_currency),
    territory: normalizeString(row.territory),
    disabled: toBoolean(row.disabled),
    creation: normalizeString(row.creation),
    modified: normalizeString(row.modified),
  };
}

function mapAddressDoc(row: AddressDoc): CustomerAddress {
  const id = normalizeString(row.name) ?? "";

  return {
    id,
    title: normalizeString(row.address_title) ?? id,
    type: normalizeString(row.address_type) ?? "Address",
    address_line1: normalizeString(row.address_line1) ?? "",
    address_line2: normalizeString(row.address_line2),
    city: normalizeString(row.city) ?? "",
    country: normalizeString(row.country) ?? "",
    is_primary: toBoolean(row.is_primary_address) || toBoolean(row.is_shipping_address),
  };
}

function compareDateString(left: string | null | undefined, right: string | null | undefined) {
  return String(left ?? "").localeCompare(String(right ?? ""));
}

function isSubmittedInvoice(invoice: SalesInvoiceListItem) {
  return invoice.status === "Submitted";
}

function isOpenReceivableInvoice(invoice: SalesInvoiceListItem) {
  return isSubmittedInvoice(invoice) && Number(invoice.outstandingAmount ?? 0) > 0;
}

function isActiveCoupon(coupon: Coupon) {
  const validFrom = normalizeString(coupon.valid_from);
  const validUpto = normalizeString(coupon.valid_upto);
  const today = todayDate();
  const used = toNumber(coupon.used);
  const maximumUse = toNumber(coupon.maximum_use);

  return (!validFrom || validFrom <= today)
    && (!validUpto || validUpto >= today)
    && (!maximumUse || used < maximumUse);
}

function mapPaymentAmount(payment: PaymentEntryListItem) {
  return payment.paymentType === "Receive"
    ? toNumber(payment.receivedAmount)
    : toNumber(payment.paidAmount);
}

function buildReferencedItems(
  rows: SalesInvoiceItemRow[],
  invoices: SalesInvoiceListItem[],
): CustomerReferencedItem[] {
  const invoiceById = new Map(invoices.map(invoice => [invoice.id, invoice]));
  const itemMap = new Map<string, CustomerReferencedItem>();

  for (const row of rows) {
    const itemCode = normalizeString(row.item_code) ?? "Unknown Item";
    const parent = normalizeString(row.parent);
    const invoice = parent ? invoiceById.get(parent) : null;
    const existing = itemMap.get(itemCode);

    if (existing) {
      existing.invoiceCount += parent ? 1 : 0;
      existing.qty += toNumber(row.qty);
      existing.amount += toNumber(row.amount);

      if (compareDateString(invoice?.postingDate, existing.lastPurchaseDate) > 0) {
        existing.lastPurchaseDate = invoice?.postingDate ?? null;
        existing.lastInvoiceId = parent;
      }
      continue;
    }

    itemMap.set(itemCode, {
      itemCode,
      itemName: normalizeString(row.item_name),
      invoiceCount: parent ? 1 : 0,
      qty: toNumber(row.qty),
      amount: toNumber(row.amount),
      lastInvoiceId: parent,
      lastPurchaseDate: invoice?.postingDate ?? null,
    });
  }

  return [...itemMap.values()]
    .sort((left, right) => right.amount - left.amount)
    .slice(0, CUSTOMER_PREVIEW_LIMIT);
}

function buildCustomerFilters(filter: CustomerFilterParam) {
  const filters: FrappeFilterClause[] = [
    ...(filter.customerType
      ? [[CUSTOMER_DOCTYPE, "customer_type", "=", filter.customerType] as FrappeFilterClause]
      : []),
    ...(filter.customerGroup
      ? [[CUSTOMER_DOCTYPE, "customer_group", "like", `%${filter.customerGroup}%`] as FrappeFilterClause]
      : []),
    ...(filter.disabled !== null && filter.disabled !== undefined
      ? [[CUSTOMER_DOCTYPE, "disabled", "=", Number(filter.disabled)] as FrappeFilterClause]
      : []),
    ...(filter.createdFrom
      ? [[CUSTOMER_DOCTYPE, "creation", ">=", filter.createdFrom] as FrappeFilterClause]
      : []),
    ...(filter.createdTo
      ? [[CUSTOMER_DOCTYPE, "creation", "<=", filter.createdTo] as FrappeFilterClause]
      : []),
  ];
  const orFilters = buildFrappeLikeSearchFilters(
    ["name", "customer_name", "mobile_no", "email_id"],
    filter.search,
    CUSTOMER_DOCTYPE,
  );

  return {
    filters: serializeFrappeFilters(filters) ?? filter.filters,
    or_filters: serializeFrappeFilters(orFilters) ?? filter.or_filters,
  };
}

async function getDefaultLinkedRecord(
  doctype: string,
  preferredNames: string[],
): Promise<string | null> {
  const service = new HttpClient(encodeDoctype(doctype));
  const response = await service.get<{ data?: GenericDocRow[] }>(
    "",
    {
      limit_start: 0,
      limit_page_length: 50,
      order_by: "modified desc",
    },
    {},
    JSON.stringify(["name", "is_group"]),
    undefined,
    FrappeMethodType.resource,
  );

  const rows = response?.data ?? [];
  const leafRows = rows.filter(row => !toBoolean(row.is_group));

  for (const preferredName of preferredNames) {
    const matched = leafRows.find(row => String(row.name ?? "") === preferredName);
    if (matched?.name)
      return String(matched.name);
  }

  return normalizeString(leafRows[0]?.name ?? rows[0]?.name);
}

function mapCustomerOption(raw: Record<string, any>): FrappeDoctypeLookupOption {
  const customerName = normalizeString(raw.customer_name);
  const mobileNo = redactGuardianPhone(raw.mobile_no) || undefined;
  const id = String(raw.name ?? "");

  return {
    id,
    label: customerName || id,
    subtitle: mobileNo || id,
    raw,
  };
}

async function fetchCustomerDoc(name: string) {
  const response = await customerService.get<any>(
    "frappe.client.get",
    {},
    {
      doctype: CUSTOMER_DOCTYPE,
      name,
    },
    undefined,
    undefined,
    FrappeMethodType.method,
  );

  return response?.message ?? response?.data ?? response ?? null;
}

async function fetchSalesInvoiceItems(invoiceIds: string[]) {
  const parentIds = [...new Set(invoiceIds.map(id => id.trim()).filter(Boolean))];
  if (!parentIds.length)
    return [] as SalesInvoiceItemRow[];

  const filters: FrappeFilterClause[] = [
    [SALES_INVOICE_ITEM_DOCTYPE, "parent", "in", parentIds],
  ];

  const response = await salesInvoiceItemService.get<{ data?: SalesInvoiceItemRow[] }>(
    "",
    {
      filters: serializeFrappeFilters(filters),
      limit_start: 0,
      limit_page_length: Math.max(parentIds.length * 20, 100),
      order_by: "modified desc",
    },
    {},
    JSON.stringify(["parent", "item_code", "item_name", "qty", "amount"]),
  );

  return response?.data ?? [];
}

/**
 * The ONE remaining raw coupon read, and it is deliberate.
 *
 * Every coupon WRITE now goes through `pet_app.api.coupons.*`. This read cannot: it needs
 * "the coupons belonging to ONE customer", and `list_coupons` takes only
 * `status | limit | cursor | search` - there is no customer parameter. Filtering a page
 * of results client-side would under-report the moment a customer has more coupons than
 * one page, and these numbers feed the customer summary counts.
 *
 * So it stays a doctype query until `list_coupons` accepts `customer`, at which point it
 * should move onto it. Recorded as a backend gap rather than papered over.
 */
async function fetchCustomerCoupons(customerName: string) {
  const filters: FrappeFilterClause[] = [
    [COUPON_DOCTYPE, "customer", "=", customerName],
  ];

  const response = await couponService.get<{ data?: Coupon[] }>(
    "",
    {
      filters: serializeFrappeFilters(filters),
      limit_start: 0,
      limit_page_length: CUSTOMER_ACTIVITY_LIMIT,
      order_by: "modified desc",
    },
    {},
    JSON.stringify([
      "name",
      "coupon_name",
      "coupon_code",
      "coupon_type",
      "pricing_rule",
      "valid_from",
      "valid_upto",
      "maximum_use",
      "used",
      "description",
      "customer",
    ]),
  );

  return response?.data ?? [];
}

async function buildCustomerFinancialSummary(
  customerName: string,
  includePreviewData: boolean,
): Promise<CustomerFinancialSummary> {
  const [invoiceResult, paymentResult, orderResult, coupons] = await Promise.all([
    GetSalesInvoices({ value: false } as Ref<boolean>, {
      customer: customerName,
      limit_start: 0,
      limit_page_length: CUSTOMER_ACTIVITY_LIMIT,
      order_by: "posting_date desc",
    }),
    GetPaymentEntries({ value: false } as Ref<boolean>, {
      filters: [
        ["Payment Entry", "party_type", "=", "Customer"],
        ["Payment Entry", "party", "=", customerName],
      ],
      limit_start: 0,
      limit_page_length: CUSTOMER_ACTIVITY_LIMIT,
      order_by: "posting_date desc",
    }),
    fetchOrders({
      customerId: customerName,
      page: 1,
      itemsPerPage: 1,
    }).catch(() => ({ data: [], total: 0 })),
    fetchCustomerCoupons(customerName).catch(() => []),
  ]);

  const invoices = invoiceResult?.data ?? [];
  const payments = paymentResult?.data ?? [];
  const submittedInvoices = invoices.filter(isSubmittedInvoice);
  const openInvoices = invoices.filter(isOpenReceivableInvoice);
  const today = todayDate();
  const upcomingDueDates = openInvoices
    .map(invoice => invoice.dueDate)
    .filter((date): date is string => Boolean(date && date >= today))
    .sort((left, right) => left.localeCompare(right));
  const receivedPayments = payments.filter(payment => payment.paymentType === "Receive");
  const invoiceItems = includePreviewData
    ? await fetchSalesInvoiceItems(
        invoices
          .filter(invoice => invoice.status !== "Cancelled")
          .map(invoice => invoice.id),
      ).catch(() => [])
    : [];

  return {
    salesOrderCount: orderResult.total,
    invoiceCount: invoiceResult?.total ?? invoices.length,
    submittedInvoiceCount: submittedInvoices.length,
    draftInvoiceCount: invoices.filter(invoice => invoice.status === "Draft").length,
    openInvoiceCount: openInvoices.length,
    totalBought: submittedInvoices.reduce((sum, invoice) => sum + toNumber(invoice.total), 0),
    totalOutstanding: openInvoices.reduce((sum, invoice) => sum + toNumber(invoice.outstandingAmount), 0),
    overdueOutstanding: openInvoices
      .filter(invoice => Boolean(invoice.dueDate && invoice.dueDate < today))
      .reduce((sum, invoice) => sum + toNumber(invoice.outstandingAmount), 0),
    nextDueDate: upcomingDueDates[0] ?? null,
    lastInvoiceDate: invoices[0]?.postingDate ?? null,
    lastInvoiceId: invoices[0]?.id ?? null,
    totalReceived: receivedPayments.reduce((sum, payment) => sum + mapPaymentAmount(payment), 0),
    paymentCount: paymentResult?.total ?? payments.length,
    lastPaymentDate: payments[0]?.postingDate ?? null,
    couponCount: coupons.length,
    usedCouponCount: coupons.filter(coupon => toNumber(coupon.used) > 0).length,
    activeCouponCount: coupons.filter(isActiveCoupon).length,
    invoices: invoices.slice(0, CUSTOMER_PREVIEW_LIMIT),
    payments: payments.slice(0, CUSTOMER_PREVIEW_LIMIT),
    coupons: coupons.slice(0, CUSTOMER_PREVIEW_LIMIT),
    referencedItems: includePreviewData ? buildReferencedItems(invoiceItems, invoices) : [],
  };
}

function mergeCustomerSummary(
  customer: CustomerDetail,
  summary: CustomerFinancialSummary,
): CustomerListItem {
  return {
    ...customer,
    totalBought: summary.totalBought,
    unpaidDebt: summary.totalOutstanding,
    receivedCredit: summary.totalReceived,
    usedCoupons: summary.usedCouponCount,
    invoiceCount: summary.invoiceCount,
    openInvoiceCount: summary.openInvoiceCount,
    lastPurchaseDate: summary.lastInvoiceDate,
  };
}

function normalizeQuickCustomerCreateError(error: any) {
  const haystack = [
    error?.message,
    error?.data?.message,
    JSON.stringify(error?.data?.details ?? ""),
  ]
    .filter(Boolean)
    .join(" ");

  if (
    /Expected text or file-like object/i.test(haystack)
    || /pypika\.functions\.IfNull/i.test(haystack)
    || /set_loyalty_program|get_loyalty_programs|loyalty_program/i.test(haystack)
  ) {
    return new Error(
      "Customer creation is currently blocked by backend loyalty-program setup. Please ask backend to fix or disable that loyalty query first.",
    );
  }

  return error instanceof Error
    ? error
    : new Error(String(error?.message ?? "Failed to create customer."));
}

export async function CreateQuickCustomer(
  loading: Ref<boolean>,
  payload: QuickCustomerPayload,
): Promise<FrappeDoctypeLookupOption | null> {
  return withFrappeLoading(loading, async () => {
    try {
      const customerService = new HttpClient(encodeDoctype("Customer"));
      const customerGroup = await getDefaultLinkedRecord("Customer Group", ["All Customer Groups"]);
      const territory = await getDefaultLinkedRecord("Territory", ["All Territories"]);

      if (!customerGroup)
        throw new Error("No default customer group is available.");

      if (!territory)
        throw new Error("No default territory is available.");

      const response = await customerService.post<CustomerDocResponse, Record<string, unknown>>(
        {
          customer_name: payload.customerName.trim(),
          customer_type: "Individual",
          customer_group: customerGroup,
          territory,
          mobile_no: normalizeString(payload.mobileNo),
          email_id: normalizeString(payload.emailId),
        },
        "",
        {},
        undefined,
        FrappeMethodType.resource,
      );

      const raw = {
        name: response?.data?.name ?? response?.name ?? "",
        customer_name: response?.data?.customer_name ?? response?.customer_name ?? payload.customerName.trim(),
        mobile_no: response?.data?.mobile_no ?? response?.mobile_no ?? normalizeString(payload.mobileNo),
        email_id: response?.data?.email_id ?? response?.email_id ?? normalizeString(payload.emailId),
      };

      if (!raw.name)
        return null;

      return mapCustomerOption(raw);
    } catch (error: any) {
      throw normalizeQuickCustomerCreateError(error);
    }
  });
}

/**
 * A lean customer list for the offline pack: only the columns the till shows,
 * and nothing derived.
 *
 * WHY NOT `GetCustomers`. That one enriches every row with
 * `buildCustomerFinancialSummary` — invoices, payment entries, orders and
 * coupons, four requests PER CUSTOMER. Fine for a 10-row page; for the ~2,000
 * customers an offline pack downloads it would be thousands of requests and
 * minutes of waiting, to compute balances the till does not show in its picker.
 *
 * Phone numbers stay redacted through the same helper the list uses, so a user
 * who may not see guardian phones does not get them cached on their device.
 */
export async function GetCustomersForOfflinePack(
  pageSize = 500,
  maxPages = 20,
): Promise<OfflineCustomerRow[]> {
  const rows: OfflineCustomerRow[] = [];

  for (let page = 0; page < maxPages; page += 1) {
    const response = await customerService.get<{ data?: CustomerDoc[] }>(
      "",
      {
        limit_start: page * pageSize,
        limit_page_length: pageSize,
        order_by: "modified desc",
        filters: JSON.stringify([[CUSTOMER_DOCTYPE, "disabled", "=", 0]]),
      },
      {},
      JSON.stringify([
        "name",
        "customer_name",
        "customer_name_in_arabic",
        "mobile_no",
        "customer_group",
        "modified",
      ]),
    );

    const batch = response?.data ?? [];

    for (const row of batch) {
      const name = String(row?.name ?? "").trim();

      if (!name)
        continue;

      rows.push({
        name,
        customerName: String(row.customer_name ?? name).trim(),
        // Read straight off the row: the display layer decides which name to show.
        arabicName: (row as Record<string, unknown>).customer_name_in_arabic
          ? String((row as Record<string, unknown>).customer_name_in_arabic).trim()
          : null,
        mobileNo: redactGuardianPhone(row.mobile_no) || null,
        customerGroup: row.customer_group ? String(row.customer_group).trim() : null,
        modified: row.modified ? String(row.modified) : null,
      });
    }

    if (batch.length < pageSize)
      break;
  }

  return rows;
}

export async function GetCustomers(
  loading: Ref<boolean>,
  filter: CustomerFilterParam = {},
): Promise<FrappeListResult<CustomerListItem> | null> {
  return withFrappeLoading(loading, async () => {
    try {
      const params = buildCustomerFilters(filter);
      const response = await customerService.get<{ data?: CustomerDoc[] }>(
        "",
        {
          limit_start: filter.limit_start ?? 0,
          limit_page_length: filter.limit_page_length ?? 10,
          order_by: filter.order_by ?? "modified desc",
          ...(params.filters ? { filters: params.filters } : {}),
          ...(params.or_filters ? { or_filters: params.or_filters } : {}),
        },
        {},
        JSON.stringify([
          "name",
          "customer_name",
          "customer_type",
          "customer_group",
          "mobile_no",
          "email_id",
          "image",
          "default_currency",
          "territory",
          "disabled",
          "creation",
          "modified",
        ]),
        undefined,
        FrappeMethodType.resource,
      );

      const total = await fetchFrappeDocCount(
        customerService,
        CUSTOMER_DOCTYPE,
        params.filters,
        params.or_filters,
      ).catch(() => response?.data?.length ?? 0);
      const normalized = normalizeFrappeListResult(response, total);
      const customers = normalized.data.map(normalizeCustomerDoc);
      const summaries = await Promise.all(
        customers.map(customer =>
          buildCustomerFinancialSummary(customer.name, false).catch(() => null),
        ),
      );

      return {
        data: customers.map((customer, index) => mergeCustomerSummary(
          customer,
          summaries[index] ?? {
            salesOrderCount: 0,
            invoiceCount: 0,
            submittedInvoiceCount: 0,
            draftInvoiceCount: 0,
            openInvoiceCount: 0,
            totalBought: 0,
            totalOutstanding: 0,
            overdueOutstanding: 0,
            nextDueDate: null,
            lastInvoiceDate: null,
            lastInvoiceId: null,
            totalReceived: 0,
            paymentCount: 0,
            lastPaymentDate: null,
            couponCount: 0,
            usedCouponCount: 0,
            activeCouponCount: 0,
            invoices: [],
            payments: [],
            coupons: [],
            referencedItems: [],
          },
        )),
        total: normalized.total,
      };
    } catch (error) {
      console.error("Failed to get customers:", error);
      return null;
    }
  });
}

export async function GetCustomerDetail(
  loading: Ref<boolean>,
  customerName: string,
): Promise<CustomerDetail | null> {
  return withFrappeLoading(loading, async () => {
    try {
      const doc = await fetchCustomerDoc(customerName);

      return doc ? normalizeCustomerDoc(doc) : null;
    } catch (error) {
      console.error("Failed to get customer detail:", error);
      return null;
    }
  });
}

export async function UpdateCustomer(
  loading: Ref<boolean>,
  customerName: string,
  payload: UpdateCustomerPayload,
): Promise<CustomerDetail | null> {
  return withFrappeLoading(loading, async () => {
    try {
      const response = await customerService.put<any, Record<string, unknown>>(
        "",
        customerName,
        {
          customer_name: payload.customerName,
          customer_type: payload.customerType || undefined,
          customer_group: payload.customerGroup || undefined,
          mobile_no: payload.mobileNo || undefined,
          email_id: payload.emailId || undefined,
          image: payload.image || null,
          default_currency: payload.defaultCurrency || undefined,
          territory: payload.territory || undefined,
          disabled: payload.disabled ? 1 : 0,
        },
        {},
        {},
        FrappeMethodType.resource,
      );
      const doc = response?.data ?? response;

      return doc ? normalizeCustomerDoc(doc) : await GetCustomerDetail({ value: false } as Ref<boolean>, customerName);
    } catch (error) {
      console.error("Failed to update customer:", error);
      return null;
    }
  });
}

export async function GetCustomerFinancialSummary(
  loading: Ref<boolean>,
  customerName: string,
): Promise<CustomerFinancialSummary | null> {
  return withFrappeLoading(loading, async () => {
    try {
      return await buildCustomerFinancialSummary(customerName, true);
    } catch (error) {
      console.error("Failed to get customer financial summary:", error);
      return null;
    }
  });
}

export async function GetCustomerAddresses(
  loading: Ref<boolean>,
  customerName: string,
): Promise<CustomerAddress[]> {
  return withFrappeLoading(loading, async () => {
    if (!customerName)
      return [];

    try {
      // Query Address directly, filtered by its Dynamic Link child rows. Frappe
      // auto-joins the `Dynamic Link` child table when a filter references it, so
      // this returns the linked addresses in a single request. (The previous
      // two-step approach queried the `Dynamic Link` child doctype via the list
      // endpoint, which does not return child rows and always came back empty.)
      const filters: FrappeFilterClause[] = [
        [DYNAMIC_LINK_DOCTYPE, "link_doctype", "=", CUSTOMER_DOCTYPE],
        [DYNAMIC_LINK_DOCTYPE, "link_name", "=", customerName],
      ];
      const addressResponse = await addressService.get<{ data?: AddressDoc[] }>(
        "",
        {
          filters: serializeFrappeFilters(filters),
          limit_start: 0,
          limit_page_length: 100,
        },
        {},
        JSON.stringify([
          "name",
          "address_title",
          "address_type",
          "address_line1",
          "address_line2",
          "city",
          "country",
          "is_primary_address",
          "is_shipping_address",
        ]),
      );

      return (addressResponse?.data ?? []).map(mapAddressDoc);
    } catch (error) {
      console.error("Failed to get customer addresses:", error);
      return [];
    }
  });
}

function buildAddressBody(payload: AddressPayload): Record<string, unknown> {
  return {
    address_title: payload.title.trim(),
    address_type: normalizeString(payload.type) ?? "Billing",
    address_line1: payload.address_line1.trim(),
    address_line2: normalizeString(payload.address_line2),
    city: normalizeString(payload.city),
    country: normalizeString(payload.country),
    is_primary_address: payload.is_primary ? 1 : 0,
    is_shipping_address: payload.is_primary ? 1 : 0,
  };
}

export async function CreateAddress(
  loading: Ref<boolean>,
  customerName: string,
  payload: AddressPayload,
): Promise<CustomerAddress | null> {
  return withFrappeLoading(loading, async () => {
    if (!customerName || !payload.address_line1.trim())
      return null;

    try {
      const response = await addressService.post<{ data?: AddressDoc }, Record<string, unknown>>(
        {
          ...buildAddressBody(payload),
          // Link the new address to its customer via the Address `links`
          // child table (Dynamic Link rows), matching how addresses are read.
          links: [
            {
              link_doctype: CUSTOMER_DOCTYPE,
              link_name: customerName,
            },
          ],
        },
        "",
        {},
        {},
        FrappeMethodType.resource,
      );

      const doc = response?.data;

      return doc ? mapAddressDoc(doc) : null;
    } catch (error) {
      console.error("Failed to create address:", error);
      return null;
    }
  });
}

export async function UpdateAddress(
  loading: Ref<boolean>,
  addressId: string,
  payload: AddressPayload,
): Promise<CustomerAddress | null> {
  return withFrappeLoading(loading, async () => {
    if (!addressId || !payload.address_line1.trim())
      return null;

    try {
      const response = await addressService.put<{ data?: AddressDoc }, Record<string, unknown>>(
        "",
        encodeURIComponent(addressId),
        buildAddressBody(payload),
        {},
        {},
        FrappeMethodType.resource,
      );

      const doc = response?.data;

      return doc ? mapAddressDoc(doc) : null;
    } catch (error) {
      console.error("Failed to update address:", error);
      return null;
    }
  });
}

